import { cloneDeep } from 'lodash';
import { getLocaleStore } from '@/i18n';
import { getTextLayerProps } from '@/utils/layer-kit';
import { TextMeasurer } from '@/utils/text-kit';
import {
    LayerType,
    TextAlignType,
    TextWritingType,
    type ILayerProps,
    type ISize,
    type ITextLayerProps,
} from '@/version-types';
import { DEFAULT_FONT_FAMILY } from '@/version-types/font';
import {
    DEFAULT_FONT_COLOR,
    DEFAULT_LINE_HEIGHT_FACTOR,
    DEFAULT_TEXT_EN,
    DEFAULT_TEXT_GRADIENT,
    DEFAULT_TEXT_ZH,
    MIN_FONT_SIZE,
} from '../constants';

export type PureTextKind = 'title' | 'subTitle' | 'content';

export const getFontSize = (kind: PureTextKind, stageSize: ISize) => {
    switch (kind) {
        case 'title':
            return Number(Math.round(stageSize.width / 21).toFixed(0)) * 2;
        case 'subTitle':
            return Number(Math.round(stageSize.width / 38).toFixed(0)) * 2;
        case 'content':
            return Number(Math.round(stageSize.width / 55).toFixed(0)) * 2;
    }
};

/**
 * 创建文本图层
 * @param stageSize - 舞台尺寸
 * @param layerData - 覆盖默认文本图层的 layerData
 */
export const createTextLayer = async (
    stageSize: ISize,
    layerData: Partial<ITextLayerProps>,
    options: {
        /** 文本位置, 默认居中 */
        position?: {
            top?: number;
            left?: number;
        };
        /** 图层尺寸 */
        layerSize?: {
            width: number;
            height: number;
        };
        fontSubsetLoader?: (text: string, fontFamily: string) => Promise<string | null>;
    } = {},
) => {
    const { width: stageWidth, height: stageHeight } = stageSize;

    const locale = getLocaleStore().locale;
    const defaultText = locale === 'en' ? DEFAULT_TEXT_EN : DEFAULT_TEXT_ZH;

    const defaultFontSize = getFontSize('subTitle', stageSize);
    const finalFontSize = Math.max(MIN_FONT_SIZE, layerData.fontSize || defaultFontSize);
    const defaultFontFamily = DEFAULT_FONT_FAMILY().name;

    const DEFAULT_LAYER_DATA: ITextLayerProps = {
        text: defaultText,
        bold: false,
        color: DEFAULT_FONT_COLOR,
        fontFamily: defaultFontFamily,
        fontSize: finalFontSize,
        italic: false,
        letterSpacing: 0,
        lineSpacing: DEFAULT_LINE_HEIGHT_FACTOR,
        lineThrough: false,
        underline: false,
        textAlign: TextAlignType.CENTER,
        writingMode: TextWritingType.HORIZONTAL_TB,
        textGlow: [],
        textShadow: [],
        textStroke: [],
        textBackground: [],
        listStyle: '',
        textGradient: DEFAULT_TEXT_GRADIENT,
        textContents: [],
    };

    const finalLayerData: ITextLayerProps = { ...DEFAULT_LAYER_DATA, ...layerData };

    let layerHeight = finalLayerData.fontSize * DEFAULT_LINE_HEIGHT_FACTOR;
    let layerWidth = 0;

    const layer = getTextLayerProps({
        height: layerHeight,
        flipH: false,
        flipV: false,
        materialIds: {},
        data: finalLayerData,
        extraInfo: {
            modified: false,
        },
        __runtime: {
            isInit: true, // ATTENTION: 本地创建的图层 __runtime.isInit 为 true
        },
    });

    const { fontSubsetLoader } = options;
    if (fontSubsetLoader) {
        const targetFontFamily = finalLayerData.fontFamily;
        const targetText = finalLayerData.text;
        const subset = await fontSubsetLoader(targetText, targetFontFamily);
        if (subset) {
            const runtimeData = cloneDeep(layer.__runtime || {});
            const fontFamilyMap = runtimeData.fontFamilyMap || {};
            const nextFontFamilyMap = { ...fontFamilyMap, [targetFontFamily]: subset };
            runtimeData.fontFamilyMap = nextFontFamilyMap;
            layer.__runtime = runtimeData;
        }
    }

    if (options.layerSize) {
        const { width, height } = options.layerSize;
        layer.width = width;
        layer.height = height;

        const isHorizontal = finalLayerData.writingMode === TextWritingType.HORIZONTAL_TB;
        // 如果竖排文字，则保持高度不变，宽度自适应
        // 如果横排文字，则保持宽度不变，高度自适应
        const measureParams = {
            keepConstantWidth: isHorizontal ? true : false,
            keepConstantHeight: isHorizontal ? false : true,
        };
        const textSize = TextMeasurer.measure(layer, measureParams);
        layerHeight = textSize.height;
        layerWidth = textSize.width;
    } else {
        const textSize = TextMeasurer.measure(layer, {
            keepConstantWidth: false,
            keepConstantHeight: true,
        });
        layerWidth = textSize.width;
    }

    const position = {
        left: (stageWidth - layerWidth) / 2,
        top: (stageHeight - layerHeight) / 2,
    };

    const { position: externalPosition = {} } = options;
    if (externalPosition.left !== undefined) position.left = externalPosition.left;
    if (externalPosition.top !== undefined) position.top = externalPosition.top;

    layer.width = layerWidth;
    layer.height = layerHeight;
    layer.left = position.left;
    layer.top = position.top;

    return layer;
};

/**
 * 验证输入（api 入参）图层的合法性
 * @param layer - 组图层（子图层全部为文本图层）或文本图层
 * @returns
 */
export const validateInputLayer = (layer: ILayerProps | null) => {
    if (!layer) return false;
    if (layer.layerType === LayerType.TEXT_LAYER) return true;
    if (layer.layerType === LayerType.GROUP_LAYER) {
        const children = layer.children;
        if (children.length === 0) return false;
        return children.every((child) => child.layerType === LayerType.TEXT_LAYER);
    }
    return false;
};
